import { Palette } from "../starterTemplates/colorPalettes";
import { GlobalColor } from "@builder/components/general/ColorInput";


const normaliseHex = (hex: string): string => {
    if (!hex) return "#000000";
    let c = hex.trim().replace("#", "");
    if (c.length === 3) {
        c = c.split("").map(ch => ch + ch).join("");
    }
    // force length 6
    if (c.length !== 6) {
        while (c.length < 6) c += "0";
        c = c.slice(0, 6);
    }
    return `#${c}`.toLowerCase();
};

const hexToRgb = (hex: string): [number, number, number] => {
    const h = normaliseHex(hex).replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return [r, g, b];
};

const getLuminance = (hex: string): number => {
    const [r, g, b] = hexToRgb(hex).map(v => v / 255);
    // Linearised RGB values
    const linear = (c: number): number => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const R = linear(r);
    const G = linear(g);
    const B = linear(b);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

const contrastRatio = (hexA: string, hexB: string): number => {
    const L1 = getLuminance(hexA);
    const L2 = getLuminance(hexB);
    const lighter = Math.max(L1, L2);
    const darker = Math.min(L1, L2);
    return (lighter + 0.05) / (darker + 0.05);
};



 const chooseDarkestColor = (palette: Palette): string => {
    return palette.colors
        .slice()
        .sort((a, b) => getLuminance(a.color) - getLuminance(b.color))[0].color;
};

 const chooseLightestColor = (palette: Palette): string => {
    return palette.colors
        .slice()
        .sort((a, b) => getLuminance(b.color) - getLuminance(a.color))[0].color;
};




// Basic RGB to HSL (only hue is needed for ordering)
const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return [h * 360, s, l];
};




export const getContrastingSpectrumColors = (
    colors: GlobalColor[],
    max: number,
    backgroundColor: string,
    minContrast: number = 1.3
): GlobalColor[] => {
    // Helper vibrancy
    const vibrancyScore = (s: number, l: number) => s * (1 - Math.abs(0.5 - l));

    // Filter by contrast
    const contrasted = colors
        .filter(c => contrastRatio(c.color, backgroundColor) >= minContrast)
        .map(c => {
            const [hueVal, sat, light] = rgbToHsl(...hexToRgb(c.color));
            return { ...c, hue: hueVal, s: sat, l: light, vib: vibrancyScore(sat, light) };
        });

    if (contrasted.length <= max) {
        // sort for display: vib desc then lum asc
        contrasted.sort((a, b) => (b.vib !== a.vib ? b.vib - a.vib : getLuminance(a.color) - getLuminance(b.color)));
        return contrasted;
    }

    // Compute mean HSL (hue via vector average)
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const cosSum = contrasted.reduce((s, c) => s + Math.cos(toRad(c.hue || 0)), 0);
    const sinSum = contrasted.reduce((s, c) => s + Math.sin(toRad(c.hue || 0)), 0);
    const meanHue = (Math.atan2(sinSum, cosSum) * 180) / Math.PI + 360 % 360;
    const meanS = contrasted.reduce((s, c) => s + c.s, 0) / contrasted.length;
    const meanL = contrasted.reduce((s, c) => s + c.l, 0) / contrasted.length;

    // Distance in HSL space (weighted). Hue distance circular.
    const hueDist = (h1: number, h2: number) => {
        const d = Math.abs(h1 - h2) % 360;
        return d > 180 ? 360 - d : d; // 0-180
    };

    contrasted.sort((a, b) => {
        const distA = hueDist(a.hue, meanHue) + Math.abs(a.s - meanS) * 100 + Math.abs(a.l - meanL) * 100;
        const distB = hueDist(b.hue, meanHue) + Math.abs(b.s - meanS) * 100 + Math.abs(b.l - meanL) * 100;
        return distA - distB;
    });

    const seen = new Set<string>();
    const picked: typeof contrasted = [];
    for (const c of contrasted) {
        if (!seen.has(c.color.toLowerCase())) {
            seen.add(c.color.toLowerCase());
            picked.push(c);
        }
        if (picked.length === max) break;
    }

    picked.sort((a, b) => (b.vib !== a.vib ? b.vib - a.vib : getLuminance(a.color) - getLuminance(b.color)));

    return picked;
};


export const determineBackgroundColor = (palette: Palette): string => {
    const isDark = palette.tags.includes("dark");
    return isDark ? chooseDarkestColor(palette) : chooseLightestColor(palette);
};